import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { supabase } from '@/lib/supabase';
import { fetchAppMode } from '@/services/appModeService';
import { LoadingScreen } from '@/components/LoadingScreen';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

/**
 * OnboardingGuard - Prüft ob der User das Onboarding abgeschlossen hat.
 *
 * Kriterien für Redirect zu /onboarding:
 * 1. Profil nicht vervollständigt (user_role nicht gesetzt)
 * 2. ODER: App-Mode ist 'idle' (keine aktive Schwangerschaft und keine Geburt)
 * 3. ODER: App-Mode ist 'pregnancy' aber keine aktive Schwangerschaft existiert
 *
 * KEIN Redirect wenn:
 * - App-Mode ist 'baby' (nach Geburt - User sieht Baby Dashboard)
 * - App-Mode ist 'pregnancy' und aktive Schwangerschaft existiert
 */
export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { user, isLoading: authLoading } = useAuth();
  const { currentPregnancy, setPregnancy, setLoading, appMode, setAppMode } = usePregnancyStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [hasFetched, setHasFetched] = useState(false);
  const [hasCompletedProfile, setHasCompletedProfile] = useState<boolean | null>(null);
  const [appModeLoaded, setAppModeLoaded] = useState(false);

  // Fetch pregnancy data, profile completion status, and app mode
  useEffect(() => {
    async function fetchData() {
      if (authLoading || !user || hasFetched) {
        return;
      }

      setLoading(true);

      try {
        // Fetch profile to check if onboarding was completed (user_role is set)
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('user_role')
          .eq('id', user.id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error fetching profile:', profileError);
        }

        // Profile is complete if user_role is set
        const profileComplete = !!profile?.user_role;
        setHasCompletedProfile(profileComplete);

        // Fetch app mode from database
        const mode = await fetchAppMode(user.id);
        setAppMode(mode);
        setAppModeLoaded(true);

        // If app mode is baby (after birth), don't fetch pregnancy - user sees Baby Dashboard
        if (mode === 'baby') {
          setPregnancy(null);
          setHasFetched(true);
          return;
        }

        // If app mode is idle, user needs onboarding - but continue to check for pregnancies
        // in case the database state is inconsistent

        // If pregnancy is already loaded, skip pregnancy fetch
        if (currentPregnancy) {
          setHasFetched(true);
          return;
        }

        // First check if user has own pregnancy
        const { data: ownPregnancies, error: ownError } = await supabase
          .from('pregnancies')
          .select('*')
          .eq('created_by', user.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1);

        if (ownError) {
          console.error('Error fetching own pregnancy:', ownError);
        }

        if (ownPregnancies && ownPregnancies.length > 0) {
          setPregnancy(ownPregnancies[0]);
          setHasFetched(true);
          return;
        }

        // Check if user is connected as partner
        try {
          const { data: connections, error: connError } = await supabase
            .from('partner_connections')
            .select('pregnancy_id')
            .eq('user_2_id', user.id)
            .eq('status', 'active')
            .limit(1);

          if (!connError && connections && connections.length > 0 && connections[0].pregnancy_id) {
            const { data: partnerPregnancies, error: partnerError } = await supabase
              .from('pregnancies')
              .select('*')
              .eq('id', connections[0].pregnancy_id)
              .eq('is_active', true)
              .limit(1);

            if (!partnerError && partnerPregnancies && partnerPregnancies.length > 0) {
              setPregnancy(partnerPregnancies[0]);
              setHasFetched(true);
              return;
            }
          }
        } catch (connErr) {
          console.error('Error checking partner connection:', connErr);
        }

        // No pregnancy found
        setPregnancy(null);
        setHasFetched(true);
      } catch (err) {
        console.error('Error fetching data:', err);
        setPregnancy(null);
        setHasFetched(true);
        setAppModeLoaded(true);
      }
    }

    fetchData();
  }, [user, authLoading, currentPregnancy, hasFetched, setPregnancy, setLoading, setAppMode]);

  // Handle redirect
  useEffect(() => {
    // Don't redirect while still loading
    if (authLoading || !hasFetched || hasCompletedProfile === null || !appModeLoaded) {
      return;
    }

    // Skip redirect if already on onboarding
    if (location.pathname.startsWith('/onboarding')) {
      return;
    }

    // If app mode is baby (after birth), don't redirect - user sees Baby Dashboard
    if (appMode === 'baby') {
      return;
    }

    // Check if user needs onboarding:
    // 1. Profile not completed (user_role not set - happens when partner abandons onboarding), OR
    // 2. App mode is 'idle' (no active pregnancy and no birth), OR
    // 3. App mode is 'pregnancy' but no active pregnancy exists (never created one)
    const needsOnboarding = !hasCompletedProfile || appMode === 'idle' || (appMode === 'pregnancy' && (!currentPregnancy || !currentPregnancy.is_active));

    if (needsOnboarding && user) {
      navigate('/onboarding', { replace: true });
    }
  }, [user, currentPregnancy, authLoading, hasFetched, hasCompletedProfile, appModeLoaded, appMode, location.pathname, navigate]);

  // Show loading while checking
  // Note: We only check authLoading, hasFetched, and appModeLoaded
  // because these are the critical states we need before making a redirect decision
  if (authLoading || !hasFetched || hasCompletedProfile === null || !appModeLoaded) {
    return <LoadingScreen />;
  }

  // If app mode is baby (after birth), allow rendering - user sees Baby Dashboard
  if (appMode === 'baby') {
    return <>{children}</>;
  }

  // If app mode is idle, user needs onboarding (will redirect)
  if (appMode === 'idle') {
    return <LoadingScreen text="Weiterleitung zum Onboarding..." />;
  }

  // If user needs onboarding, don't render children (will redirect)
  const needsOnboarding = !hasCompletedProfile || (appMode === 'pregnancy' && (!currentPregnancy || !currentPregnancy.is_active));
  if (needsOnboarding && user && !location.pathname.startsWith('/onboarding')) {
    return <LoadingScreen text="Weiterleitung..." />;
  }

  return <>{children}</>;
}
